import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { forTemplate } from '@trello/legacy-i18n';
import { Spinner } from '@trello/nachos/spinner';
import type { SecureString } from '@trello/privacy';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_SECURE_STRING,
} from '@trello/privacy';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { usePluginApplicationQuery } from '../queries/PluginApplicationQuery.generated';
import { AllowedOrigins } from './AllowedOrigins';
import { PowerUpGenerateApiKeyPage } from './PowerUpGenerateApiKeyPage';
import { TextFieldDescription } from './TextFieldDescription';

import styles from './PowerUpApiKeyPage.less';

const format = forTemplate('app_management');

export const PowerUpApiKeyPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, refetch } = usePluginApplicationQuery({
    variables: {
      pluginId: idPlugin,
    },
    waitOn: ['None'],
  });
  const [apiKey, setApiKey] = useState<string>('');
  const [secret, setSecret] = useState<SecureString>(EMPTY_SECURE_STRING);
  const [generated, setGenerated] = useState<boolean>(false);
  const onGenerate = useCallback(
    () => {
      refetch();
      setGenerated(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (data && !loading) {
      setApiKey(data.pluginApplication?.key || '');
      setSecret(data.pluginApplication?.secret || EMPTY_SECURE_STRING);
      setGenerated(true);
    }
  }, [data, loading]);

  if (loading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner
          testId={getTestId<AppManagementTestIds>(
            'power-up-appkey-page-spinner',
          )}
        />
      </div>
    );
  }

  if (!generated) {
    return (
      <PowerUpGenerateApiKeyPage idPlugin={idPlugin} onGenerate={onGenerate} />
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>{format('api-key')}</h1>
      </div>
      <div className={styles.formElements}>
        <TextFieldDescription
          label={format('api-key')}
          value={apiKey}
          description={format('api-key-management-description', { apiKey })}
        />

        <AllowedOrigins
          apiKey={apiKey}
          origins={data?.pluginApplication?.allowedOrigins || []}
        />

        <TextFieldDescription
          label={format('secret')}
          value={dangerouslyConvertPrivacyString(secret)}
          description={`<p>${format('app-secret-trello-supports-oauth')}</p>
            <br /><p>${format('please-keep-your-api-secret-safe')}</p>`}
        />
      </div>
    </div>
  );
};
